import React, { useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { useMenuLabel } from '@/lib/menu-labels';
import ComingSoonPage from '@/pages/ComingSoonPage';

/**
 * Guards the Dropshop routes with the admin's "Dropshop" menu switch.
 *
 * Switching the entry off used to only hide it from the navigation — the URL still served the
 * full page to anyone who had it bookmarked or was linked to it. Off now means closed.
 */
const DropshopGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const menuLabel = useMenuLabel();
  const dropshopOn = useAdminStore((s) => s.settings?.menuVisibility?.dropshop);
  const settingsFresh = useAdminStore((s) => s.settingsFresh);

  // Settings are fetched once per page load, so an admin who flipped the switch had to reload
  // the whole site before this page agreed. Re-asking on entry keeps the wait to one request,
  // and only on this page — the rest of the site is untouched.
  useEffect(() => {
    void useAdminStore.getState().refreshSettings();
  }, []);

  // Until the server has answered at least once, the value in hand came from the local cache
  // and may be the old one. Showing nothing for that moment is better than showing the page
  // and yanking it away, or flashing "Coming Soon" over a section that is in fact open.
  if (!settingsFresh) return <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700" />;

  if (dropshopOn === false) {
    return (
      <ComingSoonPage
        label={menuLabel('dropshop', t.dropshop.title)}
        title={'Coming\nSoon'}
        subtitle={t.dropshop.comingSoonSubtitle}
        icon={Sparkles}
      />
    );
  }

  return <>{children}</>;
};

export default DropshopGate;
